import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Building2, CalendarRange, ChevronDown, Filter, Landmark, Plus, Receipt, Route } from 'lucide-react'
import { toast } from 'sonner'

import { financeApi, type SettlementInvoiceRow } from '@/services/finance'
import { orgWalletApi, getOrgWalletLabel } from '@/services/org-wallet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { useOrganizationDateLocale } from '@/lib/org-date-locale'
import { formatServerDateForLocale, useTenantTimeZone } from '@/lib/tenant-date-time'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { getErrorMessage } from '@/lib/api'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/utils'
import { SettlementPaymentFlowDiagram } from '@/components/finance/settlement-payment-flow-diagram'
import { SettlementNotificationsSection } from '@/components/finance/settlement-notifications-section'

type SettlementInvoicesResponse = {
  count: number
  results: SettlementInvoiceRow[]
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: '1', label: 'Pending' },
  { value: '2', label: 'Partial' },
  { value: '3', label: 'Completed' },
  { value: '4', label: 'Rolled over' },
] as const

const STATUS_BADGE: Record<number, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  1: 'outline',
  2: 'secondary',
  3: 'default',
  4: 'secondary',
}

const STATUS_LABELS: Record<number, string> = {
  1: 'Pending',
  2: 'Partial',
  3: 'Completed',
  4: 'Rolled over',
}

type GenerateFormState = {
  from_date: string
  to_date: string
}

type RecordPaymentFormState = {
  amount: string
  src_wallet_id: string
  dest_wallet_id: string
  payment_ref: string
  notes: string
}

const defaultGenerateForm = (): GenerateFormState => {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 7)
  return {
    from_date: start.toISOString().slice(0, 10),
    to_date: end.toISOString().slice(0, 10),
  }
}

const defaultRecordForm = (pending = ''): RecordPaymentFormState => ({
  amount: pending,
  src_wallet_id: '',
  dest_wallet_id: '',
  payment_ref: '',
  notes: '',
})

export default function SettlementInvoicesPage() {
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const organizationDateLocale = useOrganizationDateLocale(serviceInstanceId)
  const settlementTimeZone = useTenantTimeZone(serviceInstanceId)

  const initialStatus = searchParams.get('status') || 'all'
  const initialPage = Number(searchParams.get('page') || '1')
  const initialPageSize = Number(searchParams.get('page_size') || '10')

  const [page, setPage] = useState(Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1)
  const [pageSize, setPageSize] = useState(Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10)
  const [statusFilter, setStatusFilter] = useState(initialStatus)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [generateForm, setGenerateForm] = useState<GenerateFormState>(defaultGenerateForm)
  const [selectedInvoice, setSelectedInvoice] = useState<SettlementInvoiceRow | null>(null)
  const [recordOpen, setRecordOpen] = useState(false)
  const [recordForm, setRecordForm] = useState<RecordPaymentFormState>(defaultRecordForm())
  const [lastGenerateRunId, setLastGenerateRunId] = useState<string | null>(null)
  const [flowOpen, setFlowOpen] = useState(false)
  const deferredStatus = useDeferredValue(statusFilter)

  useEffect(() => {
    setStatusFilter(searchParams.get('status') || 'all')
    setPage(Number(searchParams.get('page') || '1') || 1)
    setPageSize(Number(searchParams.get('page_size') || '10') || 10)
  }, [searchParams])

  useEffect(() => {
    const params = new URLSearchParams()
    if (serviceInstanceId) params.set('service_instance_id', serviceInstanceId)
    if (deferredStatus !== 'all') params.set('status', deferredStatus)
    if (page > 1) params.set('page', String(page))
    if (pageSize !== 10) params.set('page_size', String(pageSize))

    const nextSearch = params.toString()
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search
    if (nextSearch !== currentSearch) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true })
    }
  }, [deferredStatus, location.pathname, location.search, navigate, page, pageSize, serviceInstanceId])

  const invoicesQuery = useQuery({
    queryKey: ['settlement-invoices', serviceInstanceId, page, pageSize, deferredStatus],
    queryFn: () =>
      financeApi.getSettlementInvoices({
        page,
        page_size: pageSize,
        service_instance_id: serviceInstanceId ?? undefined,
        status: deferredStatus !== 'all' ? Number(deferredStatus) : undefined,
      }),
    enabled: Boolean(serviceInstanceId),
  })

  const walletsQuery = useQuery({
    queryKey: ['settlement-wallets', serviceInstanceId],
    queryFn: () => orgWalletApi.listWallets(serviceInstanceId!),
    enabled: recordOpen && Boolean(serviceInstanceId),
  })

  const response = (invoicesQuery.data ?? { count: 0, results: [] }) as SettlementInvoicesResponse
  const rows = response.results ?? []

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.period += row.period_amount ?? 0
        acc.pending += row.overall_pending_amount ?? 0
        acc.paid += row.amount_paid ?? 0
        acc.jobs += row.jobs_count ?? 0
        return acc
      },
      { period: 0, pending: 0, paid: 0, jobs: 0 },
    )
  }, [rows])

  const generateMutation = useMutation({
    mutationFn: () => financeApi.generateSettlementInvoices(generateForm),
    onSuccess: (payload) => {
      const runId = payload?.run_id as string | undefined
      toast.success(
        runId
          ? `Generated ${payload?.generated_count ?? 0} settlement invoice(s). Run ${runId.slice(0, 8)}…`
          : `Generated ${payload?.generated_count ?? 0} settlement invoice(s)`,
      )
      if (runId) setLastGenerateRunId(runId)
      setGenerateOpen(false)
      queryClient.invalidateQueries({ queryKey: ['settlement-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['notification-delivery-logs'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const recordMutation = useMutation({
    mutationFn: () => {
      if (!selectedInvoice) throw new Error('No invoice selected')
      return financeApi.recordSettlementPayment({
        settlement_invoice_id: selectedInvoice.id,
        amount: Number(recordForm.amount),
        src_wallet_id: recordForm.src_wallet_id || undefined,
        dest_wallet_id: recordForm.dest_wallet_id || undefined,
        payment_ref: recordForm.payment_ref,
        notes: recordForm.notes,
        service_instance_id: serviceInstanceId ?? undefined,
      })
    },
    onSuccess: () => {
      toast.success('Settlement payment recorded')
      setRecordOpen(false)
      setSelectedInvoice(null)
      queryClient.invalidateQueries({ queryKey: ['settlement-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['job-payment-summary'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openRecordDialog = (invoice: SettlementInvoiceRow) => {
    setSelectedInvoice(invoice)
    setRecordForm(defaultRecordForm(String(invoice.overall_pending_amount ?? '')))
    setRecordOpen(true)
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Settlements</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Period invoices between order booking companies and job execution companies. Customer COD and worker
            remittances are handled separately on company payments.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-lg"
            onClick={() => setFlowOpen((current) => !current)}
          >
            <Route className="mr-2 h-4 w-4" />
            Settlement flow
            <ChevronDown className={cn('ml-2 h-4 w-4 transition-transform', flowOpen && 'rotate-180')} />
          </Button>
          <Button variant="outline" asChild>
            <Link to={withServiceInstanceId('/job/company/payments', serviceInstanceId)}>
              <Receipt className="mr-2 h-4 w-4" />
              Worker cash
            </Link>
          </Button>
          <Button onClick={() => setGenerateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Generate period
          </Button>
        </div>
      </div>

      <Collapsible open={flowOpen} onOpenChange={setFlowOpen}>
        <CollapsibleContent className="pb-1">
          <SettlementPaymentFlowDiagram />
        </CollapsibleContent>
      </Collapsible>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Period amount (page)</p>
            <p className="mt-2 text-2xl font-semibold">{formatCurrency(summary.period)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Pending (page)</p>
            <p className="mt-2 text-2xl font-semibold">{formatCurrency(summary.pending)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Paid (page)</p>
            <p className="mt-2 text-2xl font-semibold">{formatCurrency(summary.paid)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Jobs (page)</p>
            <p className="mt-2 text-2xl font-semibold">{summary.jobs}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Period invoices &amp; payments</h2>
            <p className="text-sm text-muted-foreground">
              Generated inter-company invoices for this instance. Use <strong>Record payment</strong> for lump-sum
              settlement against pending balance.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(1) }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {invoicesQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : !serviceInstanceId ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Select a service instance to view settlement invoices.</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No settlement invoices for this filter.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>From → To</TableHead>
                      <TableHead>Jobs</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Carry-over</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Pending</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {row.start_date && row.end_date
                            ? `${formatServerDateForLocale(row.start_date, organizationDateLocale, settlementTimeZone)} – ${formatServerDateForLocale(row.end_date, organizationDateLocale, settlementTimeZone)}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className="inline-flex items-center gap-1 text-muted-foreground">
                            <Building2 className="h-3.5 w-3.5" />
                            {row.from_instance_id} → {row.to_instance_id}
                          </span>
                        </TableCell>
                        <TableCell>{row.jobs_count}</TableCell>
                        <TableCell>{formatCurrency(row.period_amount)}</TableCell>
                        <TableCell>{formatCurrency(row.previous_balance)}</TableCell>
                        <TableCell>{formatCurrency(row.amount_paid)}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(row.overall_pending_amount)}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_BADGE[row.status] ?? 'outline'}>
                            {STATUS_LABELS[row.status] ?? row.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={(row.overall_pending_amount ?? 0) <= 0 || row.status === 3}
                            onClick={() => openRecordDialog(row)}
                          >
                            Record payment
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <DataTablePagination
                page={page}
                pageSize={pageSize}
                totalCount={response.count ?? 0}
                onPageChange={setPage}
                onPageSizeChange={(next) => { setPageSize(next); setPage(1) }}
              />
            </>
          )}
        </CardContent>
      </Card>

      <SettlementNotificationsSection
        serviceInstanceId={serviceInstanceId}
        runIdFilter={lastGenerateRunId}
      />

      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate settlement invoices</DialogTitle>
            <DialogDescription>
              Roll up open inter-company ledger entries for the selected date range.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="from_date">From date</Label>
              <Input
                id="from_date"
                type="date"
                value={generateForm.from_date}
                onChange={(event) => setGenerateForm((current) => ({ ...current, from_date: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="to_date">To date</Label>
              <Input
                id="to_date"
                type="date"
                value={generateForm.to_date}
                onChange={(event) => setGenerateForm((current) => ({ ...current, to_date: event.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)}>Cancel</Button>
            <Button onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
              <CalendarRange className="mr-2 h-4 w-4" />
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record settlement payment</DialogTitle>
            <DialogDescription>
              Lump-sum payment from instance {selectedInvoice?.from_instance_id} to {selectedInvoice?.to_instance_id}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={recordForm.amount}
                onChange={(event) => setRecordForm((current) => ({ ...current, amount: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Source wallet (optional)</Label>
              <Select
                value={recordForm.src_wallet_id || 'none'}
                onValueChange={(value) => setRecordForm((current) => ({ ...current, src_wallet_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select wallet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(walletsQuery.data ?? []).map((wallet) => (
                    <SelectItem key={wallet.id} value={String(wallet.id)}>
                      {getOrgWalletLabel(wallet)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Destination wallet (optional)</Label>
              <Select
                value={recordForm.dest_wallet_id || 'none'}
                onValueChange={(value) => setRecordForm((current) => ({ ...current, dest_wallet_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select wallet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(walletsQuery.data ?? []).map((wallet) => (
                    <SelectItem key={wallet.id} value={String(wallet.id)}>
                      {getOrgWalletLabel(wallet)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payment_ref">Reference</Label>
              <Input
                id="payment_ref"
                value={recordForm.payment_ref}
                onChange={(event) => setRecordForm((current) => ({ ...current, payment_ref: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={recordForm.notes}
                onChange={(event) => setRecordForm((current) => ({ ...current, notes: event.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordOpen(false)}>Cancel</Button>
            <Button onClick={() => recordMutation.mutate()} disabled={recordMutation.isPending}>
              <Landmark className="mr-2 h-4 w-4" />
              Record payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
